import { Button, TextInput, Text, Tooltip, ActionIcon, Stack, Group } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useState } from "react";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { useMsal } from "@azure/msal-react";
import BasePage from "../BasePage";
import { coreApi } from "../../utils/CoreAPI";
import { IconCheck, IconEye, IconSend, IconX } from "@tabler/icons-react";
import dayjs from "dayjs";

function SendEmailPage() {
    const [passwordFieldType, setPasswordFieldType] = useState<string>("password");
    const [loading, setLoading] = useState<boolean>(false);
    const { instance } = useMsal();

    const form = useForm({
        mode: "uncontrolled",
        initialValues: {
            to: '',
            firstName: '',
            loginEmail: '',
            password: '',
            startDate: dayjs().format('YYYY-MM-DD'),
        },

        validate: {
            to: (value) => {
                if (!/^\S+@\S+\.\S+$/.test(value)) return 'Invalid email';
                return null;
            },

            firstName: (value) => {
                if (value.trim().length < 2) return 'First name too short';
                return null;
            },

            loginEmail: (value) => {
                if (!/^\S+@\S+\.\S+$/.test(value)) return 'Invalid email';
                return null;
            },

            password: (value) => {
                if (value.length < 1) return 'Password too short';
                return null;
            },
        },
    });

    const handleSendEmail = async (data: any) => {
        setLoading(true);

        const response = await coreApi.post("/it-service/mail/new-employee", data)

        if (!response.error) {
            notifications.show({
                color: "green",
                icon: <IconCheck size={24} />,
                title: "Send E-Mail",
                message: "E-Mail has been successfully sent to the user",
                withBorder: true
            });
        }
        else {
            notifications.show({
                color: "red",
                icon: <IconX size={24} />,
                title: "Send E-Mail",
                message: "Error occurred, check the console and network tab for more information",
                withBorder: true
            })
        }

        form.reset();
        setLoading(false);
    };

    const testEmail = async () => {
        const email = instance.getActiveAccount()?.username;
        const name = instance.getActiveAccount()?.name;
        const todaysDate = new Date().toLocaleDateString("en-US");

        const jsonData = {
            "to": email,
            "firstName": name,
            "loginEmail": email,
            "password": "SomeRandomPassword123!",
            "startDate": todaysDate,
        }

        setLoading(true);

        const response = await coreApi.post("/it-service/mail/new-employee", jsonData)

        if (!response.error) {
            notifications.show({
                color: "green",
                icon: <IconCheck size={24} />,
                title: "Test E-Mail",
                message: "Test completed successfully, check your mailbox",
                withBorder: true
            });
        }
        else {
            notifications.show({
                color: "red",
                icon: <IconX size={24} />,
                title: "Test E-Mail",
                message: "Error occurred, check the console and network tab for more information",
                withBorder: true
            })
        }

        setLoading(false);
    };

    return (
        <BasePage>
            <form onSubmit={form.onSubmit((values) => handleSendEmail(values))}>
                <Stack>
                    <TextInput
                        withAsterisk
                        label="Personal Email"
                        placeholder="janedoe@gmail.com"
                        description="This will be used to send the user their temporary login details."
                        key={form.key('to')}
                        {...form.getInputProps('to')}
                    />

                    <TextInput
                        withAsterisk
                        label="First Name"
                        placeholder="Jane"
                        key={form.key('firstName')}
                        {...form.getInputProps('firstName')}
                    />

                    <TextInput
                        withAsterisk
                        label="Login Email"
                        placeholder="j.doe@experiorheadoffice.ca"
                        key={form.key('loginEmail')}
                        {...form.getInputProps('loginEmail')}
                    />
                    <TextInput
                        withAsterisk
                        type={passwordFieldType}
                        label="Login Password"
                        placeholder="************"
                        key={form.key('password')}
                        {...form.getInputProps('password')}
                        rightSection={
                            <ActionIcon
                                className="unify-action-icon-subtle"
                                onMouseDown={() => setPasswordFieldType("text")}
                                onMouseUp={() => setPasswordFieldType("password")}
                                style={{
                                    width: "16px",
                                    height: "16px"
                                }}>
                                <IconEye size={"1.125rem"} />
                            </ActionIcon>
                        }
                    />

                    <DatePickerInput
                        withAsterisk
                        label="Start Date"
                        placeholder="Select user start date"
                        key={form.key('startDate')}
                        {...form.getInputProps('startDate')}
                    />

                    <Group>
                        <Button
                            w={150}
                            type="submit"
                            loading={loading}
                            loaderProps={{ type: 'dots', color: 'var(--button-normal-text' }}
                            rightSection={<IconSend size={"1.125rem"} />}>
                            Send
                        </Button>

                        <Tooltip label="Send an email to the currently logged in user" position="bottom">
                            <Button
                                ml={"sm"}
                                w={150}
                                loading={loading}
                                loaderProps={{ type: 'dots', color: 'var(--button-normal-text' }}
                                onClick={testEmail}>
                                Test E-Mail
                            </Button>
                        </Tooltip>
                    </Group>

                    <Group>
                        <Text>Current User: </Text>
                        <Text>{instance.getActiveAccount()?.username}</Text>
                    </Group>
                </Stack>
            </form>
        </BasePage>
    )
}

export default SendEmailPage;